"use client";

/**
 * Блок службы поддержки в сайдбаре.
 *
 * Блок адресный: тому, кому поддержка не заказана, он не рисуется вовсе.
 *
 * Школам из карты constants/supportContacts и участникам школ, чей менеджер завёл каналы
 * в профиле (school_contacts), показывается список каналов: кликабельно само название,
 * а номер телефона, если он есть, печатается как есть.
 *
 * Номер из school.phone со ссылкой в WhatsApp — контракт старого фронта
 * (SideContactComponent), но, в отличие от него, только участникам «Астана дарыны»:
 * телефон заполнен у многих школ, а поддержку по нему заказывала одна. Номеров у неё
 * два, WhatsApp есть на обоих — их и печатает getSchoolPhoneSupportNumbers.
 */

import type { ReactNode } from "react";
import { useTranslations } from "next-intl";

import { useGetMySchoolContactsQuery, useGetUserQuery } from "@/api/auth/authPrivateApi";
import { SupportContactLink } from "@/components/SupportContactLink";
import { WhatsappIcon } from "@/components/icons";
import { SidebarGroup } from "@/components/ui/sidebar";
import {
  getSchoolPhoneSupportNumbers,
  getSchoolSupportContacts,
  getSupportContacts,
  isSchoolPhoneSupportVisible,
} from "@/constants/supportContacts";
import { isSchoolSupportContactsEnabled } from "@/lib/branding";
import { isStudent } from "@/src/lib/auth";
import { cn } from "@/src/lib/utils";
import { useAppSelector } from "@/store";

function SupportCard({ hint, children }: { hint?: string; children: ReactNode }) {
  const t = useTranslations();

  return (
    <SidebarGroup className="group-data-[collapsible=icon]:hidden">
      {/* Фон фирменный, как у чипа пользователя ниже, но вдвое бледнее: sidebar-accent —
          это цвет ховера пунктов меню, на градиенте сайдбара он читается как чужой серый. */}
      <div
        className={cn(
          "rounded-lg px-5 py-4 text-sidebar-accent-foreground",
          "bg-[linear-gradient(90deg,rgba(0,89,255,0.08)_0%,rgba(178,11,255,0.08)_100%)]",
          "border border-white/60"
        )}
      >
        <div className="mb-4 text-center">
          <div className="text-sm font-semibold">{t("label-technical-support")}</div>
          {/* Подсказка отдельной строкой: иначе она в узком сайдбаре рвёт заголовок по словам. */}
          {hint ? <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div> : null}
        </div>

        {children}
      </div>
    </SidebarGroup>
  );
}

/* Название мессенджера одинаково во всех локалях — намеренно не ключ i18n. */
function SupportPhoneLink({ phone }: { phone: string }) {
  return (
    <div className="flex gap-3 text-sm font-bold">
      <WhatsappIcon size={22} className="shrink-0 text-[#25D366]" />

      {/* wa.me принимает только цифры, а номер свёрстан с плюсом и пробелами. */}
      <a
        href={`https://wa.me/${phone.replace(/\D/g, "")}`}
        target="_blank"
        rel="noopener noreferrer"
        className="transition-colors hover:text-[#25D366]"
      >
        <div>{phone}</div>
        <span className="ml-1 text-xs font-normal text-muted-foreground">
          Whatsapp
        </span>
      </a>
    </div>
  );
}

export function NavSupport() {
  const t = useTranslations();
  const { user } = useAppSelector((s) => s.auth);

  /* additional приходит только в /user.json с нашим X-Requested-Fields, в сторе его
     нет; запрос уже висит в providers, RTK отдаёт его из кэша. */
  const { data: me } = useGetUserQuery();

  /* Каналы из school_contacts заводятся для участников: менеджер, который их вписал,
     своей поддержке не пишет. Карта по-прежнему видна всем, кому её заказали. */
  const schoolId = user?.school_id ?? user?.school?.id;
  const withSchoolContacts = isStudent(user) && isSchoolSupportContactsEnabled();
  const { data: schoolContacts } = useGetMySchoolContactsQuery(undefined, {
    skip: !withSchoolContacts,
  });
  const contacts = withSchoolContacts
    ? getSchoolSupportContacts({ id: schoolId, contacts: schoolContacts })
    : getSupportContacts(schoolId);
  if (contacts.length > 0) {
    /* Подсказка нужна, когда видны одни названия каналов; номер виден сам. */
    const showsPhone = contacts.some((contact) => contact.key === "phone");

    return (
      <SupportCard hint={showsPhone ? undefined : t("label-technical-support-click-hint")}>
        <div className="flex flex-col gap-2">
          {contacts.map((contact) => (
            <SupportContactLink
              key={contact.key}
              contact={contact}
              className="transition-colors hover:underline"
            />
          ))}
        </div>
      </SupportCard>
    );
  }

  /* Без этой проверки плашка всплывала везде, где у школы заполнен телефон. */
  if (!isSchoolPhoneSupportVisible(me?.additional)) return null;

  const phones = getSchoolPhoneSupportNumbers(user?.school?.phone);
  if (phones.length === 0) return null;

  return (
    <SupportCard>
      <div className="flex flex-col gap-3">
        {phones.map((phone) => (
          <SupportPhoneLink key={phone} phone={phone} />
        ))}
      </div>
    </SupportCard>
  );
}
